import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  type ReactNode,
} from "react";
import type { QuantityPrice } from "@/data/catalog";

const CART_KEY = "jmanatx_cart";

export type CartItem = {
  slug: string;
  name: string;
  tier: QuantityPrice;
  qty: number;
  imageUrl?: string;
  flavor?: string;
};

type CartState = {
  items: CartItem[];
};

type CartAction =
  | { type: "ADD"; item: CartItem }
  | { type: "REMOVE"; slug: string; tierQty: string; flavor?: string }
  | { type: "SET_QTY"; slug: string; tierQty: string; qty: number; flavor?: string }
  | { type: "CLEAR" }
  | { type: "HYDRATE"; items: CartItem[] };

function cartKey(slug: string, tierQty: string, flavor?: string) {
  return `${slug}::${tierQty}::${flavor ?? ""}`;
}

function itemKey(i: CartItem) {
  return cartKey(i.slug, i.tier.qty, i.flavor);
}

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD": {
      const addKey = itemKey(action.item);
      const idx = state.items.findIndex((i) => itemKey(i) === addKey);
      if (idx >= 0) {
        const items = [...state.items];
        items[idx] = { ...items[idx], qty: items[idx].qty + action.item.qty };
        return { items };
      }
      return { items: [...state.items, action.item] };
    }
    case "REMOVE": {
      const rk = cartKey(action.slug, action.tierQty, action.flavor);
      return { items: state.items.filter((i) => itemKey(i) !== rk) };
    }
    case "SET_QTY": {
      const sk = cartKey(action.slug, action.tierQty, action.flavor);
      if (action.qty <= 0) {
        return { items: state.items.filter((i) => itemKey(i) !== sk) };
      }
      return {
        items: state.items.map((i) => (itemKey(i) === sk ? { ...i, qty: action.qty } : i)),
      };
    }
    case "CLEAR":
      return { items: [] };
    case "HYDRATE":
      return { items: action.items };
  }
}

type CartContextValue = {
  items: CartItem[];
  totalItems: number;
  addItem: (item: CartItem) => void;
  removeItem: (slug: string, tierQty: string, flavor?: string) => void;
  setItemQty: (slug: string, tierQty: string, qty: number, flavor?: string) => void;
  clearCart: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { items: [] });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CartItem[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          dispatch({ type: "HYDRATE", items: parsed });
        }
      }
    } catch {
      /* ignore corrupt data */
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(state.items));
  }, [state.items]);

  const addItem = useCallback((item: CartItem) => dispatch({ type: "ADD", item }), []);
  const removeItem = useCallback(
    (slug: string, tierQty: string, flavor?: string) =>
      dispatch({ type: "REMOVE", slug, tierQty, flavor }),
    [],
  );
  const setItemQty = useCallback(
    (slug: string, tierQty: string, qty: number, flavor?: string) =>
      dispatch({ type: "SET_QTY", slug, tierQty, qty, flavor }),
    [],
  );
  const clearCart = useCallback(() => dispatch({ type: "CLEAR" }), []);

  const totalItems = state.items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider
      value={{ items: state.items, totalItems, addItem, removeItem, setItemQty, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
